/** @format */

import React from "react";
import { Sofa } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CategoryInterface } from "@/types/category";
import { ImageLoader } from "../../../image-loader";

interface MegaMenuProps {
  category: CategoryInterface;
  hoveredParentCategory: number | null;
  onParentCategoryHover: (categoryId: number) => void;
  onMenuEnter: () => void;
  onMenuLeave: () => void;
}

export const MegaMenu: React.FC<MegaMenuProps> = ({
  category,
  hoveredParentCategory,
  onParentCategoryHover,
  onMenuEnter,
  onMenuLeave,
}) => {
  const router = useRouter();

  const getActiveParentCategory = () =>
    category.subCategories.find((cat) => cat.id === hoveredParentCategory);

  return (
    <div
      className="
        absolute left-0 top-full
        w-[1000px] h-[500px]
        bg-background
        border border-border
        rounded-2xl
        shadow-xl
        z-50
        flex
        overflow-hidden
        backdrop-blur-sm
        transition-all duration-300
      "
      onMouseEnter={onMenuEnter}
      onMouseLeave={onMenuLeave}>
      {/* ================= LEFT COLUMN ================= */}
      <div className="w-1/4 bg-muted/40 border-r border-border flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border bg-background">
          <h3 className="text-sm font-semibold text-foreground truncate">
            {category.name}
          </h3>
        </div>

        {/* Parent Categories */}
        <div className="flex-1 p-3 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          <div className="space-y-1">
            {category.subCategories.map((subcategory) => (
              <div
                key={subcategory.id}
                onClick={() => router.push(`/danh-muc/${subcategory.slug}`)}
                onMouseEnter={() => onParentCategoryHover(subcategory.id)}
                className={`
                  cursor-pointer rounded-lg p-3
                  border transition-all duration-200
                  ${
                    hoveredParentCategory === subcategory.id
                      ? "bg-background text-primary border-border shadow-sm"
                      : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                  }
                `}>
                <div className="text-sm font-medium line-clamp-2">
                  {subcategory.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= RIGHT COLUMN ================= */}
      <div className="w-3/4 bg-background flex flex-col">
        {getActiveParentCategory() ? (
          <>
            {/* Active Parent Header */}
            <div className="p-4 border-b border-border bg-muted/30">
              <h3 className="text-base font-semibold text-foreground truncate">
                {getActiveParentCategory()?.name}
              </h3>
            </div>

            {/* Child Categories */}
            <div className="flex-1 p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
              <div className="grid grid-cols-5 gap-4">
                {getActiveParentCategory()?.subCategories?.map(
                  (childCategory) => (
                    <Link
                      key={childCategory.id}
                      href={`/danh-muc/${childCategory.slug}`}
                      className="
                        group rounded-xl p-4
                        border border-transparent
                        hover:border-border
                        hover:bg-muted
                        transition-all duration-300
                        hover:shadow-md
                        hover:scale-[1.03]
                      ">
                      <div className="flex flex-col items-center gap-3">
                        <div className="relative w-12 h-12 flex-shrink-0">
                          <ImageLoader
                            fill
                            className="rounded-lg object-cover shadow-sm group-hover:shadow-md transition-all duration-300"
                            src={childCategory.imageUrl}
                            alt={childCategory.name}
                          />
                        </div>

                        <h4
                          className="
                          text-xs font-medium text-center
                          text-foreground
                          group-hover:text-primary
                          transition-colors duration-200
                          line-clamp-2
                        ">
                          {childCategory.name}
                        </h4>
                      </div>
                    </Link>
                  ),
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Sofa className="h-8 w-8 text-primary" />
              </div>
              <p className="text-muted-foreground text-sm font-medium">
                Di chuyển chuột để khám phá bộ sưu tập
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
